import { api } from './api';

export interface UserPoints {
  totalPoints: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityAt?: string;
  level: Level;
  user?: {
    id: string;
    name: string;
    avatar?: string;
  };
}

export interface Level {
  id?: string;
  level: number;
  name: string | null;
  minPoints: number;
  maxPoints?: number | null;
  icon?: string;
  badgeImage?: string | null;
  color?: string;
  order?: number;
  nextLevel?: {
    level: number;
    name: string;
    minPoints: number;
    pointsNeeded: number;
  };
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon?: string;
  type: string;
  points: number;
  requiredValue?: number;
  isActive: boolean;
  isSecret: boolean;
  unlocked?: boolean;
  unlockedAt?: string;
}

export interface UserAchievements {
  unlocked: number;
  total: number;
  achievements: Achievement[];
}

export interface PointsHistoryEntry {
  id: string;
  points: number;
  action: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

export interface GamificationSettings {
  id: string;
  enabled: boolean;
  explanationText: string | null;
  pointValues: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

export interface GamificationPublicSettings {
  enabled: boolean;
  explanationText: string | null;
  pointValues?: Record<string, number>;
  dailyLimits?: Record<string, number>;
}

export interface LeaderboardEntry {
  rank: number;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
  points: number;
  level?: Level;
  streak?: number;
}

class GamificationService {
  // =====================
  // XP SYSTEM (PHASE 2)
  // =====================

  async getMyXpStats() {
    const { data } = await api.get('/gamification/xp/me');
    return data?.data || data;
  }

  async getXpHistory(limit: number = 50) {
    const { data } = await api.get(`/gamification/xp/history?limit=${limit}`);
    return data?.data || data;
  }

  async getTopXpEarners(
    period: 'daily' | 'weekly' | 'monthly' = 'weekly',
    limit: number = 10,
  ) {
    const { data } = await api.get(
      `/gamification/xp/top-earners?period=${period}&limit=${limit}`,
    );
    return data?.data || data;
  }

  // =====================
  // STREAKS (PHASE 2)
  // =====================

  async updateStreak(minutesStudied: number, useFreeze: boolean = false) {
    const { data } = await api.post('/gamification/streaks/update', {
      minutesStudied,
      useFreeze,
    });
    return data?.data || data;
  }

  async getMyStreak() {
    const { data } = await api.get('/gamification/streaks/me');
    return data?.data || data;
  }

  async useStreakFreeze() {
    const { data } = await api.post('/gamification/streaks/freeze');
    return data?.data || data;
  }

  async getStreakLeaderboard(limit: number = 10) {
    const { data } = await api.get(
      `/gamification/streaks/leaderboard?limit=${limit}`,
    );
    return data?.data || data;
  }

  // =====================
  // CHALLENGES (PHASE 2)
  // =====================

  async getActiveChallenges() {
    const { data } = await api.get('/gamification/challenges/active');
    return data?.data || data;
  }

  async getChallengeById(id: number) {
    const { data } = await api.get(`/gamification/challenges/${id}`);
    return data?.data || data;
  }

  async joinChallenge(challengeId: number, teamId?: string) {
    const { data } = await api.post(
      `/gamification/challenges/${challengeId}/join`,
      {
        teamId,
      },
    );
    return data?.data || data;
  }

  async createChallengeTeam(
    challengeId: number,
    name: string,
    description?: string,
  ) {
    const { data } = await api.post(
      `/gamification/challenges/${challengeId}/teams`,
      {
        name,
        description,
      },
    );
    return data?.data || data;
  }

  async getChallengeTeams(challengeId: number) {
    const { data } = await api.get(
      `/gamification/challenges/${challengeId}/teams`,
    );
    return data?.data || data;
  }

  async joinChallengeTeam(teamId: string) {
    const { data } = await api.post(`/gamification/teams/${teamId}/join`);
    return data?.data || data;
  }

  // =====================
  // REWARDS (PHASE 2)
  // =====================

  async getRewardCatalog(category?: string) {
    const params = category ? `?category=${category}` : '';
    const { data } = await api.get(`/gamification/rewards/catalog${params}`);
    return data?.data || data;
  }

  async getRewardById(id: number) {
    const { data } = await api.get(`/gamification/rewards/${id}`);
    return data?.data || data;
  }

  async redeemReward(rewardId: number, deliveryInfo?: any, notes?: string) {
    const { data } = await api.post(
      `/gamification/rewards/${rewardId}/redeem`,
      {
        deliveryInfo,
        notes,
      },
    );
    return data?.data || data;
  }

  async getMyRedemptions(limit: number = 20) {
    const { data } = await api.get(
      `/gamification/rewards/redemptions/me?limit=${limit}`,
    );
    return data?.data || data;
  }

  // =====================
  // LEADERBOARDS (PHASE 2)
  // =====================

  async getWeeklyLeaderboard(
    week?: string,
    page: number = 1,
    limit: number = 20,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (week) params.append('week', week);

    const { data } = await api.get(
      `/gamification/leaderboard/weekly?${params.toString()}`,
    );
    return data?.data || data;
  }

  async getMyWeeklyRank(week?: string) {
    const params = week ? `?week=${week}` : '';
    const { data } = await api.get(
      `/gamification/leaderboard/weekly/me${params}`,
    );
    return data?.data || data;
  }

  async getWeeklyTopPerformers(week?: string, top: number = 5) {
    const params = new URLSearchParams({ top: top.toString() });
    if (week) params.append('week', week);

    const { data } = await api.get(
      `/gamification/leaderboard/weekly/top?${params.toString()}`,
    );
    return data?.data || data;
  }

  async getAllTimeLeaderboard(
    params: {
      page?: number;
      limit?: number;
      type?: 'xp' | 'streak' | 'badges';
    } = {},
  ) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/gamification/leaderboard/all-time?${searchParams.toString()}`,
    );
    return data?.data || data;
  }

  async getMonthlyLeaderboard(month?: string, limit: number = 20) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (month) params.append('month', month);

    const { data } = await api.get(
      `/gamification/leaderboard/monthly?${params.toString()}`,
    );
    return data?.data || data;
  }

  async getMyLeaderboardPosition() {
    const { data } = await api.get('/gamification/leaderboard/me');
    return data?.data || data;
  }

  // =====================
  // DAILY COMMUNITY CHECKLIST
  // =====================

  async getDailyChecklist() {
    const { data } = await api.get('/gamification/daily-checklist');
    return data?.data || data;
  }

  // =====================
  // BADGE SHARING
  // =====================

  async shareBadge(
    achievementId: string,
    platform: 'twitter' | 'linkedin' | 'facebook' | 'whatsapp',
  ) {
    const { data } = await api.post('/gamification/badges/share', {
      achievementId,
      platform,
    });
    return data?.data || data;
  }

  // =====================
  // LEGACY METHODS (PHASE 1)
  // =====================

  // Pontos
  async getMyPoints(): Promise<UserPoints> {
    const { data } = await api.get('/gamification/points/me');
    return data?.data || data;
  }

  async getPointsHistory(limit: number = 20): Promise<PointsHistoryEntry[]> {
    // Map to new XP system
    return this.getXpHistory(limit);
  }

  // Conquistas
  async getMyAchievements(): Promise<UserAchievements> {
    const { data } = await api.get('/gamification/achievements/me');
    return data?.data || data;
  }

  async getUserAchievements(userId: string): Promise<UserAchievements> {
    const { data } = await api.get(`/gamification/achievements/user/${userId}`);
    return data?.data || data;
  }

  // Níveis
  async getAllLevels(): Promise<Level[]> {
    const { data } = await api.get('/gamification/levels');
    return data?.data || data;
  }

  // Ranking
  async getLeaderboard(
    params: {
      page?: number;
      limit?: number;
      period?: 'all' | 'monthly' | 'weekly';
    } = {},
  ): Promise<{
    data: LeaderboardEntry[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.period) searchParams.append('period', params.period);

    const { data } = await api.get(
      `/gamification/leaderboard?${searchParams.toString()}`,
    );
    return data?.data || data;
  }

  async getMyRank(): Promise<{ rank: number | null; totalPoints: number }> {
    const position = await this.getMyLeaderboardPosition();
    return {
      rank: position?.rank ?? null,
      totalPoints: position?.totalPoints || 0,
    };
  }

  // Admin methods
  async createAchievement(
    achievement: Partial<Achievement>,
  ): Promise<Achievement> {
    const { data } = await api.post('/gamification/achievements', achievement);
    return data;
  }

  async updateAchievement(
    id: string,
    achievement: Partial<Achievement>,
  ): Promise<Achievement> {
    const { data } = await api.put(
      `/gamification/achievements/${id}`,
      achievement,
    );
    return data;
  }

  async deleteAchievement(id: string): Promise<void> {
    await api.delete(`/gamification/achievements/${id}`);
  }

  async createLevel(level: Partial<Level>): Promise<Level> {
    const { data } = await api.post('/gamification/levels', level);
    return data;
  }

  async updateLevel(id: string, level: Partial<Level>): Promise<Level> {
    const { data } = await api.put(`/gamification/levels/${id}`, level);
    return data;
  }

  async deleteLevel(id: string): Promise<void> {
    await api.delete(`/gamification/levels/${id}`);
  }

  // Settings (Admin)
  async getSettings(): Promise<GamificationSettings> {
    const { data } = await api.get('/gamification/settings');
    return data?.data || data;
  }

  async updateSettings(
    settings: Partial<GamificationSettings>,
  ): Promise<GamificationSettings> {
    const { data } = await api.put('/gamification/settings', settings);
    return data?.data || data;
  }

  async getPublicSettings(): Promise<GamificationPublicSettings> {
    const { data } = await api.get('/gamification/settings/public');
    return data?.data || data;
  }
}

export const gamificationService = new GamificationService();
